import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import './css/remoteControl.css';
import ChannelPanel from './components/ChannelPanel';
import ScreenGrid   from './components/ScreenGrid';
import QuickActions from './components/QuickActions';

import { useGetScreens } from '../../queries/screens';
import { useHasHardware } from '../../hooks/useHasHardware';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useHasFeature } from '../../hooks/useHasFeature';
import FeatureUnavailable from '../../components/activation/FeatureUnavailable';
import Layout from '../Layout/Layout';
import FeatureLocked from '../SharedComponents/FeatureLocked';
import { useInHub } from '../../nav/useInHub';

/**
 * The hero page. Visual TV-remote-style control surface for the venue:
 * channels (left) → screens (center) → quick actions (right).
 *
 * State that crosses sections lives here:
 *  - activeChannelId — last channel switched (used by ChannelPanel for
 *    the purple-glow active-button highlight)
 *  - sourceByScreen — best-effort map screen_id → source label, updated
 *    whenever ChannelPanel reports a successful switch. This is local
 *    optimistic state; a future enhancement could poll Firebase RTDB
 *    for the real per-screen source.
 *
 * On mobile the Bootstrap grid stacks col-12 → col-lg-* one above each
 * other (channels → screens → quick actions).
 */
export default function RemoteControl() {
  const { t } = useTranslation();
  const inHub = useInHub();

  // cache-first (same data as useHasFeature, which reads redux a render later: that flashed the locked card)
  const { features: pkgFeatures, has } = useEntitlements();
  const { isRestaurant } = useEntitlements();
  const reduxAllowed = useHasFeature("remote_control");
  const allowed = isRestaurant ? reduxAllowed : pkgFeatures.remote_control === true;
  // Full PTN → Sources/Screens/Routing; Simple → Channels + Quick Actions only.
  const fullPtn = useHasHardware();

  // Screens are shared across all 3 sections — fetch once at the top.
  // Gated reads stay off for a package that explicitly lacks hardware_control (the backend would 402).
  const { data: screenData, isLoading: screensLoading } = useGetScreens({}, { enabled: has('hardware_control') });
  const screens = useMemo(
    () => (Array.isArray(screenData?.data) ? screenData.data
         : Array.isArray(screenData) ? screenData : []),
    [screenData]
  );

  const [activeChannelId, setActiveChannelId] = useState(null);
  const [sourceByScreen,  setSourceByScreen]  = useState({});

  // Every card on the page is gated off (hardware_control AND birthday_mode explicitly false): one calm card, and
  // no page query ran (screens is disabled above; the birthday queries live in QuickActions, which never mounts).
  const allGated = !has('hardware_control') && !has('birthday_mode');

  // Feature gate AFTER all hooks — hook order must stay stable across the
  // companyDetails async load that flips `allowed` false→true (an early return
  // before the hooks above would change the hook count between renders → crash).
  if (!allowed) return isRestaurant ? <FeatureLocked feature="remote_control" /> : <Layout padding={false}><FeatureLocked feature="remote_control" /></Layout>;

  if (allGated) return <Layout padding={false}><FeatureUnavailable /></Layout>;

  // ChannelPanel calls this on successful POST /api/channels/{id}/switch.
  // We don't know the channel name from id alone; ChannelPanel passes it
  // back via the same callback.
  const handleSwitched = (channelId, screenIds, channelName) => {
    setActiveChannelId(channelId);
    setSourceByScreen((prev) => {
      const next = { ...prev };
      for (const id of (screenIds ?? [])) next[id] = channelName ?? '✓';
      return next;
    });
  };

  return (
    <div className="remote-page">
      <div className="container-fluid">
        {!inHub && (
          <h2 className="mb-3 fw-bold" style={{ color: 'var(--pp-text-primary)' }}>
            <i className="pi pi-mobile me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('remote_control.title')}
          </h2>
        )}

        <div className="row g-3">
          {/* Simple mode (no PTN): only Channels + Quick Actions, split 50/50.
              Full mode: Channels · Screens · Quick Actions. */}
          <div className={fullPtn ? 'col-12 col-lg-4 col-xl-3' : 'col-12 col-lg-6'}>
            <ChannelPanel
              screens={screens}
              activeChannelId={activeChannelId}
              onSwitched={handleSwitched}
            />
          </div>

          {fullPtn && (
            <div className="col-12 col-lg-5 col-xl-6">
              <ScreenGrid
                screens={screens}
                loading={screensLoading}
                sourceByScreen={sourceByScreen}
                activeChannelId={activeChannelId}
              />
            </div>
          )}

          <div className={fullPtn ? 'col-12 col-lg-3 col-xl-3' : 'col-12 col-lg-6'}>
            <QuickActions screens={screens} />
          </div>
        </div>
      </div>
    </div>
  );
}
